import React, { useState, useEffect, useRef, memo } from 'react';
import { useParams } from 'react-router-dom';
import { getRaffle, getTickets, subscribeToTickets } from '../services/raffleService';
import { OnDemandLoader } from '../services/onDemandLoader';
import PremiumTicketGrid from './PremiumTicketGrid';
import PremiumHeader from './PremiumHeader';
import PremiumTicketModal from './PremiumTicketModal';
import PremiumMultiTicketModal from './PremiumMultiTicketModal';
import LoadingSpinner from './LoadingSpinner';
import toast from 'react-hot-toast';
import '../styles/premium-theme.css';

const PremiumRafflePage = () => {
  const { raffleId } = useParams();
  const [raffle, setRaffle] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState('all');
  const [searchNumber, setSearchNumber] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [showMultiModal, setShowMultiModal] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // grid, list, compact
  const containerRef = useRef(null);

  useEffect(() => {
    loadRaffle();
  }, [raffleId]);

  useEffect(() => {
    if (raffleId) {
      const unsubscribe = subscribeToTickets(raffleId, (updatedTickets) => {
        setTickets(updatedTickets);
      });
      return () => unsubscribe();
    }
  }, [raffleId]);

  const loadRaffle = async () => {
    try {
      setLoading(true);
      const raffleData = await getRaffle(raffleId);
      
      if (!raffleData) {
        toast.error('Rifa no encontrada');
        return;
      }
      
      setRaffle(raffleData);
      await OnDemandLoader.checkAndLoadTickets(raffleId);
      
      const ticketsData = await getTickets(raffleId);
      
      if (ticketsData.length === 0) {
        setTimeout(async () => {
          const updatedTickets = await getTickets(raffleId);
          setTickets(updatedTickets);
        }, 2000);
      } else {
        setTickets(ticketsData);
      }
    } catch (error) {
      console.error('Error loading raffle:', error);
      toast.error('Error al cargar la rifa');
    } finally {
      setLoading(false);
    }
  };

  const handleTicketClick = (ticket) => {
    if (ticket.sold || ticket.reserved) {
      toast.error('Este boleto ya está ocupado');
      return;
    }
    
    if (isMultiSelectMode) {
      const isSelected = selectedTickets.find(t => t.id === ticket.id);
      if (isSelected) {
        setSelectedTickets(selectedTickets.filter(t => t.id !== ticket.id));
      } else {
        setSelectedTickets([...selectedTickets, ticket]);
      }
    } else {
      setSelectedTicket(ticket);
      setShowModal(true);
    }
  };
  
  const handleRandomSelection = (count) => {
    const availableTickets = tickets.filter(t => !t.sold && !t.reserved);
    
    if (availableTickets.length === 0) {
      toast.error('No hay boletos disponibles');
      return;
    }
    
    if (count > availableTickets.length) {
      toast.error(`Solo hay ${availableTickets.length} boletos disponibles`);
      return;
    }
    
    const shuffled = [...availableTickets].sort(() => 0.5 - Math.random());
    const selected = shuffled.slice(0, count);
    
    setSearchResults(selected);
    setSearchNumber('Números aleatorios');
    
    if (isMultiSelectMode) {
      setSelectedTickets([...selectedTickets, ...selected]);
    }
  };
  
  const handleReserveSelected = () => {
    if (selectedTickets.length === 0) {
      toast.error('No has seleccionado ningún boleto');
      return;
    }
    
    setShowMultiModal(true);
  };
  
  const handleCloseMultiModal = () => {
    setShowMultiModal(false);
    setSelectedTickets([]);
    setIsMultiSelectMode(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTicket(null);
  };

  const getFilteredTickets = () => {
    switch (filter) {
      case 'available':
        return tickets.filter(t => !t.sold && !t.reserved);
      case 'sold':
        return tickets.filter(t => t.sold || t.reserved);
      default:
        return tickets;
    }
  };

  const getStats = () => {
    const sold = tickets.filter(t => t.sold).length;
    const reserved = tickets.filter(t => t.reserved && !t.sold).length;
    const available = tickets.length - sold - reserved;
    
    return { sold, reserved, available, total: tickets.length };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="text-center">
          <div className="relative">
            <div className="absolute inset-0 animate-pulse rounded-full bg-gradient-to-r from-purple-400 to-pink-400 blur-2xl opacity-75"></div>
            <LoadingSpinner />
          </div>
          <p className="mt-4 text-lg font-medium text-gray-700">Cargando experiencia premium...</p>
        </div>
      </div>
    );
  }

  if (!raffle) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="text-center bg-white rounded-2xl shadow-2xl p-8 max-w-md">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">
            Rifa no encontrada
          </h1>
          <p className="text-gray-600">
            Verifica que el link sea correcto
          </p>
        </div>
      </div>
    );
  }

  const stats = getStats();
  const filteredTickets = getFilteredTickets();

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchNumber(value);
    
    if (value.trim() === '') {
      setSearchResults([]);
      return;
    }
    
    const searchTerms = value.split(',').map(term => term.trim()).filter(term => term !== '');
    const results = tickets.filter(ticket => {
      return searchTerms.some(term => 
        ticket.displayNumbers.toLowerCase().includes(term.toLowerCase()) ||
        ticket.number.toString() === term
      );
    });
    
    setSearchResults(results);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-pink-50" ref={containerRef}>
      {/* Premium Header */}
      <PremiumHeader raffle={raffle} stats={stats} />
      
      {/* Premium Content */}
      <div className="relative">
        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 w-64 h-64 bg-purple-300 rounded-full filter blur-3xl opacity-20 -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute top-20 right-0 w-96 h-96 bg-pink-300 rounded-full filter blur-3xl opacity-20 translate-x-1/2"></div>
        
        {raffle.isPrivateMode ? (
          // Private Mode - Enhanced Search Experience
          <div className="relative z-10 max-w-5xl mx-auto px-4 py-12">
            <div className="premium-card p-8 backdrop-blur-xl bg-white/90">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <h2 className="text-3xl font-bold text-gray-800 mb-2">Modo Privado</h2>
                <p className="text-gray-600">Busca tu número para verificar disponibilidad</p>
              </div>
              
              {/* Search Input */}
              <div className="relative mb-8">
                <input
                  type="text"
                  value={searchNumber}
                  onChange={handleSearch}
                  placeholder="Buscar número (ej: 42, 15, 73)"
                  className="w-full px-6 py-4 pr-12 text-lg border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-purple-200 focus:border-purple-500 transition-all"
                />
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>
              
              {/* Quick Actions */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                <button
                  onClick={() => setIsMultiSelectMode(!isMultiSelectMode)}
                  className={`relative overflow-hidden py-4 px-6 rounded-xl font-semibold transition-all ${
                    isMultiSelectMode 
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg transform scale-105' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span className="relative z-10 flex items-center justify-center">
                    {isMultiSelectMode && (
                      <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                    {isMultiSelectMode ? 'Selección Múltiple Activa' : 'Activar Selección Múltiple'}
                  </span>
                  {isMultiSelectMode && (
                    <div className="absolute inset-0 bg-white opacity-20 animate-pulse"></div>
                  )}
                </button>
                
                {isMultiSelectMode && selectedTickets.length > 0 && (
                  <button
                    onClick={handleReserveSelected}
                    className="btn-premium bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold py-4 px-6 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
                  >
                    Reservar {selectedTickets.length} Boletos
                  </button>
                )}
              </div>
              
              {/* Lucky Numbers */}
              <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-2xl p-6 mb-8">
                <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                  <span className="text-2xl mr-2">🎲</span>
                  Números de la Suerte
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[1, 3, 5, 10].map(count => (
                    <button
                      key={count}
                      onClick={() => handleRandomSelection(count)}
                      className="group relative overflow-hidden bg-white rounded-xl py-3 px-4 font-semibold text-gray-700 shadow-md hover:shadow-xl transform hover:scale-105 transition-all"
                    >
                      <span className="relative z-10">{count} {count === 1 ? 'número' : 'números'}</span>
                      <div className="absolute inset-0 bg-gradient-to-r from-purple-400 to-pink-400 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                      <span className="absolute inset-0 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity">
                        {count} {count === 1 ? 'número' : 'números'}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Search Results */}
              {searchNumber && (
                <div className="animate-fadeIn">
                  {searchResults.length > 0 ? (
                    <>
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">
                        {searchResults.length} resultado{searchResults.length !== 1 && 's'} encontrado{searchResults.length !== 1 && 's'}
                      </h3>
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {searchResults.map(ticket => (
                          <div
                            key={ticket.id}
                            onClick={() => handleTicketClick(ticket)}
                            className={`relative group cursor-pointer transform transition-all duration-300 hover:scale-110 ${
                              selectedTickets.find(t => t.id === ticket.id) ? 'scale-105' : ''
                            }`}
                          >
                            <div className={`relative overflow-hidden rounded-2xl p-6 text-center ${
                              ticket.sold 
                                ? 'bg-gradient-to-br from-red-400 to-red-500 text-white' 
                                : ticket.reserved
                                ? 'bg-gradient-to-br from-yellow-400 to-orange-400 text-white'
                                : selectedTickets.find(t => t.id === ticket.id)
                                ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white ring-4 ring-purple-300'
                                : 'bg-gradient-to-br from-green-400 to-emerald-500 text-white hover:from-green-500 hover:to-emerald-600'
                            }`}>
                              {/* Shimmer effect */}
                              <div className="absolute inset-0 shimmer-effect opacity-30"></div>
                              
                              <div className="relative z-10">
                                <div className="text-3xl font-bold mb-1">{ticket.displayNumbers}</div>
                                <div className="text-sm opacity-90">
                                  {ticket.sold ? 'Vendido' : ticket.reserved ? 'Reservado' : 'Disponible'}
                                </div>
                              </div>
                              
                              {selectedTickets.find(t => t.id === ticket.id) && (
                                <div className="absolute top-2 right-2 w-6 h-6 bg-white rounded-full flex items-center justify-center">
                                  <svg className="w-4 h-4 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                  </svg>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12">
                      <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <p className="text-gray-500 text-lg">No se encontraron resultados para "{searchNumber}"</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Normal Mode - Full Grid Experience */
          <>
            {/* Search and Filters Bar */}
            <div className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-gray-200 shadow-sm">
              <div className="max-w-7xl mx-auto px-4 py-4">
                <div className="flex flex-wrap gap-4 items-center justify-between">
                  {/* Search */}
                  <div className="flex-1 min-w-[300px] relative">
                    <input
                      type="text"
                      value={searchNumber}
                      onChange={handleSearch}
                      placeholder="Buscar números..."
                      className="w-full pl-12 pr-4 py-3 bg-white border-2 border-gray-200 rounded-full focus:ring-4 focus:ring-purple-200 focus:border-purple-500 transition-all"
                    />
                    <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  
                  {/* View Mode Toggle */}
                  <div className="flex items-center gap-2 bg-gray-100 rounded-full p-1">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`px-4 py-2 rounded-full transition-all ${
                        viewMode === 'grid' ? 'bg-white shadow-md text-purple-600' : 'text-gray-600'
                      }`}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setViewMode('compact')}
                      className={`px-4 py-2 rounded-full transition-all ${
                        viewMode === 'compact' ? 'bg-white shadow-md text-purple-600' : 'text-gray-600'
                      }`}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                      </svg>
                    </button>
                  </div>
                  
                  {/* Multi-select Toggle */}
                  <button
                    onClick={() => setIsMultiSelectMode(!isMultiSelectMode)}
                    className={`px-6 py-3 rounded-full font-semibold transition-all ${
                      isMultiSelectMode 
                        ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg' 
                        : 'bg-white text-gray-700 border-2 border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    {isMultiSelectMode ? '✓ Selección Múltiple' : 'Selección Múltiple'}
                  </button>
                </div>
                
                {/* Filter Pills */}
                <div className="flex gap-3 mt-4">
                  <button
                    onClick={() => setFilter('all')}
                    className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                      filter === 'all' 
                        ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Todos ({stats.total})
                  </button>
                  <button
                    onClick={() => setFilter('available')}
                    className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                      filter === 'available' 
                        ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-md' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Disponibles ({stats.available})
                  </button>
                  <button
                    onClick={() => setFilter('sold')}
                    className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                      filter === 'sold' 
                        ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white shadow-md' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    Ocupados ({stats.sold + stats.reserved})
                  </button>
                  
                  {/* Lucky Numbers */}
                  <div className="ml-auto flex items-center gap-2">
                    <span className="text-sm text-gray-600">🎲</span>
                    {[1, 3, 5, 10].map(count => (
                      <button
                        key={count}
                        onClick={() => handleRandomSelection(count)}
                        className="px-3 py-2 bg-purple-100 text-purple-700 rounded-full text-sm font-medium hover:bg-purple-200 transition-colors"
                      >
                        {count}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Selected Tickets Bar */}
                {isMultiSelectMode && selectedTickets.length > 0 && (
                  <div className="mt-4 p-4 bg-gradient-to-r from-purple-100 to-pink-100 rounded-xl flex items-center justify-between">
                    <span className="font-semibold text-purple-800">
                      {selectedTickets.length} boleto{selectedTickets.length !== 1 && 's'} seleccionado{selectedTickets.length !== 1 && 's'}
                    </span>
                    <div className="flex gap-3">
                      <button
                        onClick={handleReserveSelected}
                        className="px-6 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-full font-semibold shadow-md hover:shadow-lg transform hover:scale-105 transition-all"
                      >
                        Reservar Todos
                      </button>
                      <button
                        onClick={() => setSelectedTickets([])}
                        className="px-6 py-2 bg-white text-gray-700 rounded-full font-semibold border-2 border-gray-200 hover:border-gray-300 transition-all"
                      >
                        Limpiar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Premium Ticket Grid */}
            <div className="relative z-10 max-w-7xl mx-auto px-4 py-8">
              {searchNumber ? (
                searchResults.length > 0 ? (
                  <div>
                    <h3 className="text-2xl font-bold text-gray-800 mb-6">
                      Resultados de búsqueda
                      <span className="ml-2 text-sm font-normal text-gray-600">
                        ({searchResults.length} encontrados)
                      </span>
                    </h3>
                    <PremiumTicketGrid 
                      tickets={searchResults}
                      onTicketClick={handleTicketClick}
                      selectedTickets={selectedTickets}
                      isMultiSelectMode={isMultiSelectMode}
                      viewMode={viewMode}
                    />
                  </div>
                ) : (
                  <div className="text-center py-20">
                    <div className="w-32 h-32 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <svg className="w-16 h-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <p className="text-2xl text-gray-500">No se encontraron resultados</p>
                    <p className="text-gray-400 mt-2">Intenta con otro número</p>
                  </div>
                )
              ) : (
                <PremiumTicketGrid 
                  tickets={filteredTickets}
                  onTicketClick={handleTicketClick}
                  selectedTickets={selectedTickets}
                  isMultiSelectMode={isMultiSelectMode}
                  viewMode={viewMode}
                />
              )}
            </div>
          </>
        )}
      </div>

      {/* Modals */}
      {showModal && selectedTicket && (
        <PremiumTicketModal
          ticket={selectedTicket}
          raffle={raffle}
          raffleId={raffleId}
          onClose={handleCloseModal}
        />
      )}
      
      {showMultiModal && selectedTickets.length > 0 && (
        <PremiumMultiTicketModal
          tickets={selectedTickets}
          raffle={raffle}
          raffleId={raffleId}
          onClose={handleCloseMultiModal}
        />
      )}
      
      {/* Floating Action Button for Mobile */}
      <div className="lg:hidden fixed bottom-6 right-6 z-50">
        <button
          onClick={() => setIsMultiSelectMode(!isMultiSelectMode)}
          className={`w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-all transform hover:scale-110 ${
            isMultiSelectMode
              ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
              : 'bg-white text-gray-700 border-2 border-gray-200'
          }`}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        </button>
      </div>
      
      {/* Premium Footer */}
      <footer className="relative mt-20 py-8 bg-gradient-to-r from-gray-900 to-black text-white">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center mb-4">
            <img src="/google-play-badge.svg" alt="Google Play" className="h-12 mr-4" />
            <span className="text-xl font-bold">TalonarioRifas</span>
          </div>
          <p className="text-gray-400 text-sm">
            La mejor experiencia en rifas digitales
          </p>
        </div>
      </footer>
    </div>
  );
};

// CSS Animations
const style = document.createElement('style');
style.textContent = `
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  .animate-fadeIn {
    animation: fadeIn 0.5s ease-out;
  }
`;
document.head.appendChild(style);

export default PremiumRafflePage;